import type {
  ResourceRoomProjectDetail,
  ResourceRoomProjectSummary
} from "../features/projects/resourceRoomTypes";

export type WeekPattern = "EVERY" | "ODD" | "EVEN";
export type WeekParity = "ODD" | "EVEN";
export type CourseNature = "PREFERRED" | "GENERAL" | "PROTECTED";
export type PlanStatus = "PENDING" | "SCHEDULED" | "NEEDS_CONFIRMATION";
export type LessonType = "INDIVIDUAL" | "GROUP";
export type TimeSlotSource = "IMPORTED" | "SUPPLEMENTARY";

export interface Student {
  id: string;
  import_key: string;
  display_name: string;
  grade_label: string | null;
  class_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface TimeSlot {
  id: string;
  source: TimeSlotSource;
  period_number: number | null;
  raw_label: string;
  start_minute: number;
  end_minute: number;
  sort_order: number;
  day_part: "AM" | "PM";
}

export interface OriginalCourse {
  id: string;
  name: string;
  name_key: string;
  nature: CourseNature | null;
  created_at: string;
  updated_at: string;
}

export interface StudentOriginalLesson {
  student_id: string;
  weekday: number;
  time_slot_id: string;
  original_course_id: string;
}

export interface InterventionCourse {
  id: string;
  name: string;
  name_key: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface Teacher {
  id: string;
  name: string;
  name_key: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface Room {
  id: string;
  name: string;
  name_key: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface Unavailability {
  id: string;
  scope: "SCHOOL" | "TEACHER" | "ROOM";
  target_id: string | null;
  weekday: number;
  time_slot_id: string;
}

export interface InterventionPlan {
  id: string;
  lesson_type: LessonType;
  course_id: string;
  student_ids: string[];
  weekly_sessions: number;
  consecutive_periods: number;
  week_pattern: WeekPattern;
  main_teacher_id: string;
  assistant_teacher_ids: string[];
  room_id: string;
  status: PlanStatus;
  confirmed_protection_fingerprint: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface InterventionSession {
  id: string;
  plan_id: string;
  session_index: number;
  weekday: number;
  time_slot_ids: string[];
}

export interface ResourceRoomEntities {
  students: Student[];
  time_slots: TimeSlot[];
  original_courses: OriginalCourse[];
  student_original_lessons: StudentOriginalLesson[];
  intervention_courses: InterventionCourse[];
  teachers: Teacher[];
  rooms: Room[];
  unavailability: Unavailability[];
  intervention_plans: InterventionPlan[];
  intervention_sessions: InterventionSession[];
}

export interface ResourceRoomProjectDocumentV2 {
  schema_version: 2;
  document_revision: number;
  id: string;
  name: string;
  morning_last_time_slot_id: string | null;
  created_at: string;
  updated_at: string;
  entities: ResourceRoomEntities;
}

export function normalizeProjectName(value: string): string {
  const normalized = value.trim();
  if (!normalized) throw new Error("课表名称不能为空");
  if (normalized.length > 120) throw new Error("课表名称不能超过 120 个字符");
  return normalized;
}

export function createEmptyProjectDocument(name: string): ResourceRoomProjectDocumentV2 {
  const now = new Date().toISOString();
  return {
    schema_version: 2,
    document_revision: 0,
    id: crypto.randomUUID(),
    name: normalizeProjectName(name),
    morning_last_time_slot_id: null,
    created_at: now,
    updated_at: now,
    entities: {
      students: [],
      time_slots: [],
      original_courses: [],
      student_original_lessons: [],
      intervention_courses: [],
      teachers: [],
      rooms: [],
      unavailability: [],
      intervention_plans: [],
      intervention_sessions: []
    }
  };
}

export function assertProjectDocument(
  value: unknown
): asserts value is ResourceRoomProjectDocumentV2 {
  if (!value || typeof value !== "object") throw new Error("课表项目文档格式不正确");
  const document = value as Partial<ResourceRoomProjectDocumentV2>;
  if (document.schema_version !== 2) throw new Error("课表项目版本不受支持");
  if (
    typeof document.id !== "string" ||
    typeof document.name !== "string" ||
    !Number.isInteger(document.document_revision) ||
    !document.entities
  ) {
    throw new Error("课表项目文档缺少必要字段");
  }
  const entities = document.entities as Partial<ResourceRoomEntities>;
  for (const key of entityKeys) {
    if (!Array.isArray(entities[key])) throw new Error(`课表项目缺少 ${key} 数据`);
  }
  for (const slot of entities.time_slots ?? []) {
    if (slot.source === "IMPORTED") {
      if (!Number.isInteger(slot.period_number) || Number(slot.period_number) < 1) {
        throw new Error("导入节次缺少有效编号");
      }
    } else if (slot.source === "SUPPLEMENTARY") {
      if (slot.period_number !== null) throw new Error("补充节次不能设置节次编号");
    } else {
      throw new Error("节次来源不受支持");
    }
  }
}

export function readProjectDocument(value: unknown): ResourceRoomProjectDocumentV2 {
  assertProjectDocument(value);
  return structuredClone(value);
}

export function renameProjectDocument(
  document: ResourceRoomProjectDocumentV2,
  name: string
): ResourceRoomProjectDocumentV2 {
  return {
    ...structuredClone(document),
    name: normalizeProjectName(name),
    document_revision: document.document_revision + 1,
    updated_at: new Date().toISOString()
  };
}

export function projectSummary(
  document: ResourceRoomProjectDocumentV2
): ResourceRoomProjectSummary {
  return {
    id: document.id,
    name: document.name,
    document_revision: document.document_revision,
    created_at: document.created_at,
    updated_at: document.updated_at
  };
}

export function projectDetail(
  document: ResourceRoomProjectDocumentV2
): ResourceRoomProjectDetail {
  const entities = document.entities;
  const scheduledPlanCount = entities.intervention_plans.filter(
    (plan) => plan.status === "SCHEDULED"
  ).length;
  const needsConfirmationCount = entities.intervention_plans.filter(
    (plan) => plan.status === "NEEDS_CONFIRMATION"
  ).length;
  return {
    ...projectSummary(document),
    morning_last_time_slot_id: document.morning_last_time_slot_id,
    module_counts: {
      students: entities.students.length,
      incomplete_students: entities.students.filter(
        (student) => !student.grade_label || !student.class_name
      ).length,
      time_slots: entities.time_slots.length,
      original_courses: entities.original_courses.length,
      unset_original_courses: entities.original_courses.filter((course) => !course.nature)
        .length,
      intervention_courses: entities.intervention_courses.length,
      teachers: entities.teachers.length,
      rooms: entities.rooms.length,
      plans: entities.intervention_plans.length,
      scheduled_plans: scheduledPlanCount,
      needs_confirmation: needsConfirmationCount
    },
    has_timetable: scheduledPlanCount > 0,
    pending_plan_count:
      entities.intervention_plans.length - scheduledPlanCount - needsConfirmationCount,
    needs_confirmation_count: needsConfirmationCount
  };
}

const entityKeys: Array<keyof ResourceRoomEntities> = [
  "students",
  "time_slots",
  "original_courses",
  "student_original_lessons",
  "intervention_courses",
  "teachers",
  "rooms",
  "unavailability",
  "intervention_plans",
  "intervention_sessions"
];
